import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, FilePlus2, Files, ExternalLink, BookOpen, Building2, Menu, X, Radar, Scale } from "lucide-react";

const ICON_FM = "https://customer-assets.emergentagent.com/job_subasta-deuda-report/artifacts/x01sqrqt_Icono%202.0.png";

const NAV_ITEMS = [
  { to: "/admin", label: "Panel", icon: Files, exact: true, testid: "nav-dashboard" },
  { to: "/admin/valoraciones", label: "Valoraciones", icon: Scale, testid: "nav-valoraciones" },
  { to: "/admin/crm", label: "CRM", icon: Files, testid: "nav-crm" },
  { to: "/admin/blog", label: "Blog", icon: BookOpen, testid: "nav-blog" },
  { to: "/admin/seo", label: "SEO", icon: Radar, testid: "nav-seo" },
  { to: "/admin/subastas", label: "Subastas", icon: Building2, testid: "nav-subastas" },
];

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const handleLogout = async () => { await logout(); navigate("/admin/login"); };
  const isActive = (path, exact = false) => exact
    ? location.pathname === path
    : (location.pathname === path || (path !== "/admin" && location.pathname.startsWith(path)));
  const linkClass = (path, exact = false) =>
    `px-3 py-1.5 text-xs tracking-[0.18em] uppercase font-bold transition-colors ${
      isActive(path, exact)
        ? "text-[#2d1f1a] border-b-2 border-[#E0651F]"
        : "text-stone-500 hover:text-[#2d1f1a]"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-[#2d1f1a] border-b border-[#3d2820] h-16 flex items-center px-4 sm:px-6">
      <div className="max-w-7xl mx-auto flex items-center justify-between w-full gap-3">
        <Link to="/admin" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="logo-link">
          <img src={ICON_FM} alt="FM" className="h-8 w-8 sm:h-9 sm:w-9 object-contain flex-shrink-0" />
          <div className="hidden sm:block leading-none">
            <div className="font-display font-black text-sm tracking-tight text-white">FERNANDO MARCOS</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#E0651F] font-bold mt-0.5">Subastas &amp; Investment</div>
          </div>
          <span className="hidden md:inline text-[9px] tracking-[0.3em] uppercase font-bold text-[#E0651F] ml-2 border border-[#E0651F]/40 px-2 py-0.5 rounded-sm">Admin</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 bg-white px-2 py-1 rounded-sm">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} className={linkClass(item.to, item.exact)} data-testid={item.testid}>
                <span className="inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {item.label}</span>
              </Link>
            );
          })}
          <Link to="/" className="px-3 py-1.5 text-xs tracking-[0.18em] uppercase font-bold text-stone-500 hover:text-[#2d1f1a] transition-colors" data-testid="nav-site">
            <span className="inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" /> Ver web</span>
          </Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="hidden lg:inline text-xs text-stone-300 font-mono truncate max-w-[180px]" data-testid="user-email">{user?.email}</span>
          <button
            onClick={handleLogout}
            className="hidden sm:inline-flex text-xs tracking-[0.18em] uppercase font-bold text-stone-300 hover:text-[#E0651F] items-center gap-1.5 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Salir
          </button>
          <button
            onClick={() => setMenuOpen(true)}
            className="md:hidden text-stone-200 hover:text-[#E0651F] p-2"
            aria-label="Abrir menú"
            data-testid="mobile-menu-open"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/70" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-overlay">
          <aside
            className="absolute top-0 right-0 bottom-0 w-72 max-w-[85vw] bg-[#1c130f] border-l border-[#3d2820] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#3d2820]">
              <div>
                <div className="font-display font-black text-sm text-white tracking-tight">Menú</div>
                {user?.email && <div className="text-[10px] text-stone-500 font-mono mt-0.5 truncate">{user.email}</div>}
              </div>
              <button onClick={() => setMenuOpen(false)} className="text-stone-400 hover:text-white p-1" aria-label="Cerrar menú" data-testid="mobile-menu-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to, item.exact);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`${item.testid}-mobile`}
                    className={`flex items-center gap-3 px-5 py-3 text-sm tracking-[0.15em] uppercase font-bold border-l-2 transition-colors ${
                      active
                        ? "text-[#E0651F] border-[#E0651F] bg-[#E0651F]/10"
                        : "text-stone-300 border-transparent hover:bg-stone-900 hover:text-white"
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {item.label}
                  </Link>
                );
              })}
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                data-testid="nav-site-mobile"
                className="flex items-center gap-3 px-5 py-3 text-sm tracking-[0.15em] uppercase font-bold border-l-2 border-transparent text-stone-300 hover:bg-stone-900 hover:text-white"
              >
                <ExternalLink className="w-4 h-4" /> Ver web
              </Link>
            </nav>
            <div className="border-t border-[#3d2820] px-5 py-4">
              <button
                onClick={() => { setMenuOpen(false); handleLogout(); }}
                className="w-full inline-flex items-center justify-center gap-2 text-xs tracking-[0.18em] uppercase font-bold text-white bg-stone-800 hover:bg-[#E0651F] px-4 py-2.5 rounded-sm transition-colors"
                data-testid="logout-btn-mobile"
              >
                <LogOut className="w-4 h-4" /> Cerrar sesión
              </button>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}
